import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DateTime } from 'luxon';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { PhysicalBook, Service } from '../../core/_models';
import { getBooks } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';

export function Books() {
    const axios = useAxios();

    usePageTitle('View Books');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [books, setBooks] = useState<PhysicalBook[]>([]);

    const loadServices = useCallback(async () => {
        setLoading(true);
        setLoadingError(null);
        try {
            const updatedBooks = await getBooks(axios);
            setBooks(updatedBooks);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios]);

    useEffect(() => {
        loadServices();
    }, [loadServices]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadServices}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900">
                        View Books
                    </h1>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        {/* subtitle */}
                    </div>
                </div>
                <div className="flex items-center gap-2.5">
                    <Link className="btn btn-sm btn-light" to="/create-book">
                        Create Book
                    </Link>
                </div>
            </div>
        </div>
        <div className="container-fixed">
            <div className="grid gap-5 lg:gap-7.5">
                <div className="card card-grid min-w-full">
                    <div className="card-header flex-wrap py-5">
                        <h3 className="card-title">
                            Books
                        </h3>
                        {false && <div className="flex gap-6">
                            <div className="relative">
                                <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3">
                                </i>
                                <input className="input input-sm pl-8" data-datatable-search="#teams_table" placeholder="Search Teams" type="text" />
                            </div>
                            <label className="switch switch-sm">
                                <input className="order-2" name="check" type="checkbox" value="1" />
                                <span className="switch-label order-1">
                                    Only Active Groups
                                </span>
                            </label>
                        </div>}
                    </div>
                    <div className="card-body">
                        <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                            <div className="scrollable-x-auto">
                                <table className="table table-fixed table-border" data-datatable-table="true">
                                    {books.length > 0 && <thead>
                                        <th className="w-[350px]">
                                            <span className="sort asc">
                                                <span className="sort-label">
                                                    Deceased Name
                                                </span>
                                                {false && <span className="sort-icon">
                                                </span>}
                                            </span>
                                        </th>
                                        <th className="w-[100px] text-center">
                                            <span className="sort">
                                                <span className="sort-label">
                                                    Status
                                                </span>
                                                {false && <span className="sort-icon">
                                                </span>}
                                            </span>
                                        </th>
                                        <th className="w-[100px] text-center">
                                            <span className="sort">
                                                <span className="sort-label">
                                                    PDF
                                                </span>
                                                {false && <span className="sort-icon">
                                                </span>}
                                            </span>
                                        </th>
                                        <th className="w-[200px]">
                                            <span className="sort">
                                                <span className="sort-label">
                                                    Created
                                                </span>
                                                {false && <span className="sort-icon">
                                                </span>}
                                            </span>
                                        </th>
                                    </thead>}
                                    {books.length > 0 ? <tbody>
                                        {books.map((book: PhysicalBook) => {
                                            return <tr key={book.unique_identifier}>
                                                <td>
                                                    <div className="flex flex-col gap-1.5">
                                                        <div className="flex flex-row items-center gap-2">
                                                            <Link
                                                                to={`/books/${book.unique_identifier}`}
                                                                className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                                                            >
                                                                {book.service.public_data.deceased_person.full_name}
                                                            </Link>
                                                        </div>
                                                        <span className="text-2sm text-gray-600">
                                                            {`${book.service.public_data.deceased_person.birth_date} - ${book.service.public_data.deceased_person.death_date}`}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex flex-col items-center justify-center gap-1.5 w-full" title={book.public_data.status}>
                                                        <div>
                                                            {book.public_data.status === 'ordered' && <span className="flex w-3 h-3 bg-green-500 rounded-full"></span>}
                                                            {book.public_data.status === 'ready' && <span className="flex w-3 h-3 bg-blue-500 rounded-full"></span>}
                                                            {book.public_data.status === 'processing' && <span className="flex w-3 h-3 bg-gray-500 rounded-full"></span>}
                                                            {book.public_data.status === 'failed' && <span className="flex w-3 h-3 bg-red-500 rounded-full"></span>}
                                                        </div>
                                                        <div>
                                                            {book.public_data.status}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="flex flex-col gap-1.5">
                                                        {book.public_data.output_urls ? <>
                                                            <div className="flex flex-row items-center gap-2">
                                                                <a
                                                                    className="leading-none font-semibold text-sm text-gray-900 hover:text-primary"
                                                                    href={book.public_data.output_urls.final_url_for_cover}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    View Cover
                                                                </a>
                                                            </div>
                                                            <span className="text-2sm text-gray-600">
                                                                <a
                                                                    href={book.public_data.output_urls.final_url_for_interior} target="_blank" rel="noreferrer">
                                                                    View Interior
                                                                </a>
                                                            </span>
                                                        </> : <>
                                                            <div className="flex flex-row items-center gap-2">
                                                                <span className="leading-none font-semibold text-sm text-gray-900">
                                                                    Processing
                                                                </span>
                                                            </div>
                                                        </>}
                                                    </div>
                                                </td>
                                                <td>
                                                    {DateTime.fromISO(book.inserted_at).toRelative()}
                                                </td>
                                            </tr>
                                        })}
                                    </tbody> : <tbody>
                                        <tr>
                                            <td colSpan={1} className="text-center">
                                                <div>
                                                    No books have been created.
                                                </div>
                                                <div className="mt-3">
                                                    <Link className="btn btn-sm btn-light" to="/create-service">
                                                        Create Book
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {false && "fullExample..." && <div className="container-fixed">
            <div className="grid gap-5 lg:gap-7.5">
                <div className="card card-grid min-w-full">
                    <div className="card-header flex-wrap py-5">
                        <h3 className="card-title">
                            Teams
                        </h3>
                        <div className="flex gap-6">
                            <div className="relative">
                                <i className="ki-filled ki-magnifier leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3">
                                </i>
                                <input className="input input-sm pl-8" data-datatable-search="#teams_table" placeholder="Search Teams" type="text" />
                            </div>
                            <label className="switch switch-sm">
                                <input className="order-2" name="check" type="checkbox" value="1" />
                                <span className="switch-label order-1">
                                    Only Active Groups
                                </span>
                            </label>
                        </div>
                    </div>
                    <div className="card-body">
                        <div data-datatable="true" data-datatable-page-size="10" id="teams_table">
                            <div className="scrollable-x-auto">
                                <table className="table table-fixed table-border" data-datatable-table="true">
                                    <thead>
                                        <tr>
                                            <th className="w-[60px] text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-check="true" type="checkbox" />
                                            </th>
                                            <th className="w-[350px]">
                                                <span className="sort asc">
                                                    <span className="sort-label">
                                                        Team
                                                    </span>
                                                    <span className="sort-icon">
                                                    </span>
                                                </span>
                                            </th>
                                            <th className="w-[200px]">
                                                <span className="sort">
                                                    <span className="sort-label">
                                                        Rating
                                                    </span>
                                                    <span className="sort-icon">
                                                    </span>
                                                </span>
                                            </th>
                                            <th className="w-[200px]">
                                                <span className="sort">
                                                    <span className="sort-label">
                                                        Last Modified
                                                    </span>
                                                    <span className="sort-icon">
                                                    </span>
                                                </span>
                                            </th>
                                            <th className="w-[200px]">
                                                <span className="sort asc">
                                                    <span className="sort-label">
                                                        Members
                                                    </span>
                                                    <span className="sort-icon">
                                                    </span>
                                                </span>
                                            </th>
                                            <th className="w-[60px]">
                                            </th>
                                            <th className="w-[60px]">
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="1" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Product Management
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Overseeing product development and lifecycle
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                21 Oct, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-4.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-1.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-2.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +10
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="2" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Marketing Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Developing campaigns, market analysis
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                15 Oct, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-4.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="hover:z-5 relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] uppercase text-warning-inverse ring-warning-light bg-warning">
                                                            g
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="3" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        HR Department
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Talent acquisition, employee welfare
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                10 Oct, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-4.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-1.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-2.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +A
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="4" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Sales Division
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Customer relations, sales strategy execution
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                05 Oct, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-24.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-7.png" />
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="5" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        IT Support
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Maintaining IT infrastructure, user support
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                21 Oct, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-4.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-2.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-primary-inverse ring-primary-light bg-primary">
                                                            +s
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="6" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Research and Development
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Innovating and developing new products
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                28 Sep, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-3.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-6.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +4
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="7" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Finance Department
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing company finances
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                22 Sep, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-8.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-9.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +8
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="8" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Operations Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Ensuring smooth day-to-day operations
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                15 Sep, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-10.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-11.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +5
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="9" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Legal Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Handling all legal matters
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                10 Sep, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-12.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-13.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +7
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="10" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Customer Service
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Providing customer support and assistance
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                05 Sep, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-14.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-15.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +3
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="11" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Procurement Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Sourcing and purchasing materials
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                30 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-16.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-17.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-primary-inverse ring-primary-light bg-primary">
                                                            +6
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="12" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Quality Assurance
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Ensuring product quality
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                25 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-18.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-19.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +2
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="13" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Logistics Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing supply chain and distribution
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                20 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-20.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-21.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +9
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="14" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Design Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Creating visual content and UI designs
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                15 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-22.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-23.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +4
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="15" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Technical Writing
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Documenting product features and guides
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                10 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-24.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-25.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +3
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="16" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Data Analytics
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Analyzing data and generating insights
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                05 Aug, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-26.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-27.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-primary-inverse ring-primary-light bg-primary">
                                                            +6
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="17" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Security Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Ensuring data and system security
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                30 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-28.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-29.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +2
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="18" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Admin Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Handling administrative tasks
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                25 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-30.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-31.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +3
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="19" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Customer Relations
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing customer interactions
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                20 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-32.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-33.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +7
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="20" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Training Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Training employees on new systems
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                15 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-34.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-1.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +5
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="21" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Project Management
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing company projects
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                10 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-2.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-3.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-primary-inverse ring-primary-light bg-primary">
                                                            +8
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="22" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Business Analysis
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Analyzing business processes
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                05 Jul, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-4.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-5.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +4
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="23" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Corporate Communications
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing internal and external communications
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                30 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-6.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-7.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +6
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="24" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Compliance Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Ensuring regulatory compliance
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                25 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-8.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-9.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +7
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="25" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Risk Management
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Identifying and managing risks
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                20 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-10.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-11.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +5
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="26" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Strategy Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Developing and implementing strategies
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                15 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-12.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-13.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-primary-inverse ring-primary-light bg-primary">
                                                            +6
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="27" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Executive Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Leading the company
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                10 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-14.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-15.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +8
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="28" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Social Media Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Managing social media channels
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                05 Jun, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-16.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-17.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-success-inverse ring-success-light bg-success">
                                                            +4
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="29" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Supply Chain Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Overseeing the supply chain
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                30 May, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-18.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-19.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-warning-inverse ring-warning-light bg-warning">
                                                            +5
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="text-center">
                                                <input className="checkbox checkbox-sm" data-datatable-row-check="true" type="checkbox" value="30" />
                                            </td>
                                            <td>
                                                <div className="flex flex-col gap-1.5">
                                                    <a className="leading-none font-semibold text-sm text-gray-900 hover:text-primary" href="#">
                                                        Content Team
                                                    </a>
                                                    <span className="text-2sm text-gray-600">
                                                        Creating and managing content
                                                    </span>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="rating">
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label checked">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none">
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                    <div className="rating-label indeterminate">
                                                        <i className="rating-on ki-solid ki-star text-base leading-none" style={{
                                                            width: '50.0%'
                                                        }}>
                                                        </i>
                                                        <i className="rating-off ki-outline ki-star text-base leading-none">
                                                        </i>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                25 May, 2024
                                            </td>
                                            <td>
                                                <div className="flex -space-x-2">
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-20.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <img className="hover:z-5 relative shrink-0 rounded-full ring-1 ring-light-light size-[30px]" src="/media/avatars/300-21.png" />
                                                    </div>
                                                    <div className="flex">
                                                        <span className="relative inline-flex items-center justify-center shrink-0 rounded-full ring-1 font-semibold leading-none text-3xs size-[30px] text-info-inverse ring-info-light bg-info">
                                                            +3
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-notepad-edit">
                                                    </i>
                                                </a>
                                            </td>
                                            <td>
                                                <a className="btn btn-sm btn-icon btn-clear btn-light" href="#">
                                                    <i className="ki-filled ki-trash">
                                                    </i>
                                                </a>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-5 text-gray-600 text-2sm font-medium">
                                <div className="flex items-center gap-2 order-2 md:order-1">
                                    Show
                                    <select className="select select-sm w-16" data-datatable-size="true" name="perpage">
                                    </select>
                                    per page
                                </div>
                                <div className="flex items-center gap-4 order-1 md:order-2">
                                    <span data-datatable-info="true">
                                    </span>
                                    <div className="pagination" data-datatable-pagination="true">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card">
                    <div className="card-header">
                        <h3 className="card-title">
                            FAQ
                        </h3>
                    </div>
                    <div className="card-body py-3">
                        <div data-accordion="true" data-accordion-expand-all="true">
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_1_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        How is pricing determined for each plan ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_1_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision.
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision.
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_2_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        What payment methods are accepted for subscriptions ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_2_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_3_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        Are there any hidden fees in the pricing ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_3_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_4_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        Is there a discount for annual subscriptions ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_4_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_5_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        Do you offer refunds on subscription cancellations ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_5_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                            <div className="accordion-item [&amp;:not(:last-child)]:border-b border-b-gray-200" data-accordion-item="true">
                                <button className="accordion-toggle py-4" data-accordion-toggle="#faq_6_content">
                                    <span className="text-base text-gray-900 font-medium">
                                        Can I add extra features to my current plan ?
                                    </span>
                                    <i className="ki-filled ki-plus text-gray-600 text-sm accordion-active:hidden block">
                                    </i>
                                    <i className="ki-filled ki-minus text-gray-600 text-sm accordion-active:block hidden">
                                    </i>
                                </button>
                                <div className="accordion-content hidden" id="faq_6_content">
                                    <div className="text-gray-700 text-md pb-4">
                                        Metronic embraces flexible licensing options that empower you to choose the perfect fit for your project's needs and budget.
                                        Understanding the factors influencing each plan's pricing helps you make an informed decision
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="grid lg:grid-cols-2 gap-5 lg:gap-7.5">
                    <div className="card">
                        <div className="card-body px-10 py-7.5 lg:pr-12.5">
                            <div className="flex flex-wrap md:flex-nowrap items-center gap-6 md:gap-10">
                                <div className="flex flex-col items-start gap-3">
                                    <h2 className="text-1.5xl font-semibold text-gray-900">
                                        Questions ?
                                    </h2>
                                    <p className="text-sm font-medium text-gray-700 leading-5.5 mb-2.5">
                                        Visit our Help Center for detailed assistance on billing, payments, and subscriptions.
                                    </p>
                                </div>
                                <img alt="image" className="dark:hidden max-h-[150px]" src="/media/illustrations/29.svg" />
                                <img alt="image" className="light:hidden max-h-[150px]" src="/media/illustrations/29-dark.svg" />
                            </div>
                        </div>
                        <div className="card-footer justify-center">
                            <a className="btn btn-link" href="">
                                Go to Help Center
                            </a>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body px-10 py-7.5 lg:pr-12.5">
                            <div className="flex flex-wrap md:flex-nowrap items-center gap-6 md:gap-10">
                                <div className="flex flex-col items-start gap-3">
                                    <h2 className="text-1.5xl font-semibold text-gray-900">
                                        Contact Support
                                    </h2>
                                    <p className="text-sm font-medium text-gray-700 leading-5.5 mb-2.5">
                                        Need assistance? Reach out to Justin Nalls for prompt, personalized help with your questions.
                                    </p>
                                </div>
                                <img alt="image" className="dark:hidden max-h-[150px]" src="/media/illustrations/31.svg" />
                                <img alt="image" className="light:hidden max-h-[150px]" src="/media/illustrations/31-dark.svg" />
                            </div>
                        </div>
                        <div className="card-footer justify-center">
                            <a className="btn btn-link" href="mailto:jnalls@mogulclients.com">
                                Contact Support
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>}
    </>
}